import React, { useState } from "react";
import { getBrandName } from "../getBrandName";

export const useCrossBorderData = () => {
  const [active, setActive] = useState("storage");
  const { brandName , normalizedBrand } = getBrandName();

  const sections = [
    { id: "storage", title: "Where Your Data is Stored" },
    { id: "security", title: "How We Secure Your Data in Cross-Border Transfers" },
    { id: "rights", title: "Your Rights Under Indian Law" },
    { id: "why", title: `Why ${brandName} Uses Cross-Border Data Storage` },
    { id: "sovereignty", title: "Data Sovereignty & Assurance for Indian Users" },
    { id: "contact", title: "Questions or Concerns?" },
  ];

  const handleScrollTo = (id) => {
    setActive(id);
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const generateContent = (id) => {
    const contentMap = {
      storage: `
To ensure high-performance analytics, real-time access, and redundancy, we may store and process your data on secure cloud servers outside India, such as:

• Amazon Web Services (AWS)
  • Primary data hosting may occur in regions such as Singapore, Frankfurt, or Mumbai (based on service optimization and infrastructure availability).

• Other Trusted Cloud Infrastructure Providers
  • We may work with vendors that comply with ISO 27001, SOC 2, GDPR, and other international data privacy standards.

These global providers are selected for their robust security controls, uptime guarantees, and strict compliance certifications.
`,

      security: `
Even when your data leaves India, we implement strong safeguards to maintain its confidentiality, integrity, and availability:

a. Encryption and Secure Protocols
• End-to-end encryption for data in transit (using SSL/TLS protocols) and at rest
• Use of HTTPS and encrypted APIs for communication with third-party services
• Secure VPN or tokenized access, where applicable

b. Legal & Contractual Measures
• Binding Data Processing Agreements (DPAs) with cloud vendors and third-party providers
• Access control, multi-factor authentication (MFA), and data masking techniques used for internal access
• Continuous monitoring for unauthorized access, data leakage, or cross-boundary violations

c. Regulatory Compliance
• Full alignment with India’s DPDP Act, 2023
• Adherence to global standards such as:
  • General Data Protection Regulation (GDPR)
  • ISO/IEC 27001
  • SOC 2 Type II Security Standards
`,

      rights: `
Regardless of where your data is stored, your rights remain fully intact under Indian law. In accordance with the DPDP Act, 2023, you have the right to:

• Know where your data is being stored or processed.
• Access and receive a copy of your personal data upon request.
• Correct or update your personal data if it is inaccurate.
• Request deletion of your data, subject to legal and business limitations.
• Withdraw your consent at any time, without affecting past lawful use.
• File complaints with our Grievance Officer if you believe your rights have been violated.
`,

      why: `
Storing data internationally allows us to offer a more efficient, scalable, and reliable service to you. Key benefits include:

a. Better Performance
• Shorter load times and faster data retrieval across regions.
• Efficient content delivery via global Content Delivery Networks (CDNs).

b. High Availability
• Redundant server locations for disaster recovery and failover support.
• Better uptime and service continuity even during peak load or outages.

c. Scalability & Cost-Effectiveness
• Elastic global infrastructure helps scale storage and processing power according to your evolving needs.
• Lower latency and improved analytics response times.
`,

      sovereignty: `
We take data sovereignty seriously, especially for Indian customers. While international storage is part of our optimization strategy, we ensure:

• All storage vendors are vetted for compliance with Indian data laws.
• No unauthorized third-party sharing occurs without your consent.
• You can choose to restrict your data to India-only storage if such a feature is made available in the future (please write to us for early requests).
`,

      contact: `
• Email: ${normalizedBrand}@gmail.com  
• Phone: +91- 94248 47009  
• Website: www.${normalizedBrand}.com  

We aim to respond to data-related inquiries within 72 hours and resolve formal requests within 7–15 working days, as required by law.
`,
    };

    const lines = contentMap[id]?.split("\n") || [];

    let items = [];
    const contentElements = [];

    const flushList = () => {
      if (items.length) {
        contentElements.push(
          <ul key={Math.random()}>
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
        items = [];
      }
    };

    lines.forEach((line, index) => {
      if (!line.trim()) return;

      if (/^•/.test(line) || /^  •/.test(line)) {
        items.push(line.replace(/^(\s*)•\s*/, ""));
      } else if (/^a\./i.test(line) || /^[a-z]\./i.test(line)) {
        flushList();
        contentElements.push(
          <h6 key={`subheading-${index}`} style={{ marginTop: "1rem" }}>
            {line}
          </h6>
        );
      } else {
        flushList();
        contentElements.push(
          <p key={`para-${index}`} style={{ marginBottom: "0.5rem" }}>
            {line}
          </p>
        );
      }
    });

    flushList();
    return contentElements;
  };

  return { sections, active, handleScrollTo, generateContent };
};
